import moment from 'moment';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { isArray } from 'lodash';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { ReactComponent as CloseIcon } from '@ubeya/shared-web/assets/close-big.svg';
import { ReactComponent as StarIcon } from '@ubeya/shared-web/assets/star-gold.svg';
import { ReactComponent as BasePhoneIcon } from '@ubeya/shared-web/assets/phone-big.svg';
import { ReactComponent as BasePhoneMenuIcon } from '@ubeya/shared-web/assets/chat.svg';
import { ReactComponent as FilledChatIcon } from '@ubeya/shared-web/assets/ubeya-chat-fill.svg';
import { ReactComponent as WhatsappIcon } from '@ubeya/shared-web/assets/whatsapp.svg';
import { ReactComponent as UnCheckedCalendarViewIcon } from '@ubeya/shared-web/assets/calendar-unchecked.svg';
import { ReactComponent as BalloonIcon } from '@ubeya/shared-web/assets/balloon.svg';
import { ReactComponent as IdIcon } from '@ubeya/shared-web/assets/id.svg';
import { ReactComponent as EmployeeIcon } from '@ubeya/shared-web/assets/employee.svg';
import { ReactComponent as AppMenuIcon } from '@ubeya/shared-web/assets/app.svg';

import {
  Avatar as BaseAvatar,
  card,
  CircleIcon as BaseCircleIcon,
  FlexCenter,
  FlexColumn,
  FlexMiddle,
  Header2SemiBold,
  Loader,
  Small,
  SmallSemiBold,
  TimeAgoRenderer,
  useModal,
  Header2,
  SemiExtraSmall,
  BodySemiBold
} from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useChatIframe from '@ubeya/shared-web/hooks/useChatIframe';
import useOnClickOutside from '@ubeya/shared-web/hooks/useOnClickOutside';
import usePortal from '@ubeya/shared-web/hooks/usePortal';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import { maxMedia } from '@ubeya/shared-web/style';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import { getEmployeeDisplayName } from '@ubeya/shared-web/utils/employee';
import { FieldTypes } from '@ubeya/shared/hooks/account/useFields';
import useLocations from '@ubeya/shared/hooks/account/useLocations';
import { mappedArray } from '@ubeya/shared/utils/array';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import useSuppliers from '@ubeya/shared/hooks/account/useSuppliers';
import { getEmployeeComplianceDataStatus } from '@ubeya/shared-web/utils/employee';
import useUser from '@ubeya/shared/hooks/auth/useUser';
import { PhotoGallery } from './PhotoGallery';
import { DetailsBlockChip, DetailBlockText, DetailsBlockWrapper, Chips, DetailRow } from './DetailBlock';
import Accordion from './Accordion';
import { isDocumentFieldValue } from './profileSidebarUtils';

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  background: rgba(19, 21, 41, 0.3);
  z-index: 5;
`;

const LoaderWrapper = styled(FlexCenter)`
  flex: 1;
`;

const jumpIn = keyframes`
  0% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(0);
  }
`;

const CircleIcon = styled(BaseCircleIcon)`
  padding: 0;
`;

const PhoneIcon = styled(BasePhoneIcon)`
  height: 15px;
  width: 15px;
  stroke-width: 1;
`;

const PhoneMenuIconSmall = styled(BasePhoneMenuIcon)`
  height: 15px;
  width: 15px;
`;

//Buttons styling
const CloseButton = styled(CircleIcon)`
  position: fixed;
  right: 15px;
  top: 15px;
  z-index: 1;
`;

const Container = styled(FlexColumn)`
  ${card};
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-width: 25%;
  ${maxMedia.mobile`max-width: 100%;`};
  border-radius: 8px 0 0 8px;
  animation: ${jumpIn} 300ms;
  overflow: auto;
`;

const ShortInfo = styled(FlexColumn)`
  align-items: center;
  padding: 30px 0 0;
`;

const Avatar = styled(BaseAvatar)`
  margin-bottom: 24px;
  font-size: 66rem;
`;

const Name = styled(Header2SemiBold)`
  text-align: center;
`;

const External = styled(Header2)`
  color: ${({ theme }) => theme.colors.grey4};
`;

const InfoRow = styled(FlexMiddle)`
  margin-bottom: 16px;
  gap: 8px;
`;

const RatingWrapper = styled(FlexMiddle)`
  > svg {
    margin-right: 6px;
  }

  > div {
    margin-right: 18px;
  }
`;

const LastActivity = styled(Small)`
  color: ${({ theme }) => theme.colors.grey4};
`;

const DetailBlockTitle = styled(BodySemiBold)``;

const DetailBlockSubText = styled(SemiExtraSmall)``;

const OSProfileSidebar = ({
  image,
  firstName,
  lastName,
  rating,
  phone,
  userId,
  lastActivity,
  fields: fieldsValues,
  compliance,
  fieldsMetadata = [],
  isLoading,
  onClose,
  jobSummary,
  photos = []
}) => {
  const ref = useRef();
  const { Portal } = usePortal({ enabled: true, portalId: 'modal' });
  const { accountId } = useAccount();
  const { dateFormat, isShowLastNameFirst = false } = useContext(ConfigContext);
  const { attachModal, detachModal } = useModal();
  const { accounts } = useUser();

  const { isRestrictedFromEmployees } = accounts.find(({ id }) => id === accountId) || {};

  const [isPhotoGalleryOpen, setIsPhotoGalleryOpen] = useState(false);
  const [isDocumentPreviewOpen, setIsDocumentPreviewOpen] = useState(false);

  useOnClickOutside(ref, onClose, !isPhotoGalleryOpen && !isDocumentPreviewOpen);

  const { t } = useContext(ConfigContext);
  const lastActivityProps = useMemo(() => ({ rowData: { lastActivity }, dataKey: 'lastActivity' }), [lastActivity]);

  const formattingMap = useFormattingMap();
  const { mappedLocations } = useLocations();
  const { mappedSupplierPositions } = useSuppliers();
  const fieldsMapping = useMemo(() => mappedArray(fieldsValues || []), [fieldsValues]);

  const employeeComplianceDataStatus = useMemo(
    () =>
      getEmployeeComplianceDataStatus({
        employeeComplianceData: compliance,
        mappedPositions: mappedSupplierPositions,
        employeeFields: fieldsValues,
        t
      }),
    [compliance, fieldsValues, mappedSupplierPositions, t]
  );

  const fieldValueFormatter = useCallback(
    (value, currentField) => {
      if (isArray(value)) {
        const values = value.map((valueId) => currentField?.options?.find((option) => option.id === valueId)?.title);
        return values.join(', ');
      }

      const singleValue = currentField?.options?.find((option) => option.id === value);

      if (singleValue) {
        return singleValue.title;
      }

      if (value && moment(value).isValid()) {
        return moment(value).format(dateFormat);
      }

      return value;
    },
    [dateFormat]
  );

  const getField = useCallback(
    (slug) => {
      const currentField = fieldsMetadata.find((field) => field.slug === slug || field.name === slug);
      if (currentField && fieldsMapping?.[currentField.id]) {
        const value = fieldsMapping?.[currentField.id].value;

        if (currentField?.fieldTypeId === FieldTypes.FIELD_TYPE_DOCUMENT) {
          if (isDocumentFieldValue(value)) {
            return {
              label: t(currentField.name),
              value: value.map((doc) => ({ title: doc?.title || t('document'), link: doc?.link }))
            };
          }
          return null;
        }

        const fieldValue = fieldValueFormatter(value, currentField);
        if (!fieldValue) {
          return {};
        }
        return {
          label: t(currentField.name),
          value: {
            title: fieldValueFormatter(value, currentField)
          }
        };
      }

      return null;
    },
    [fieldValueFormatter, fieldsMapping, fieldsMetadata, t]
  );

  const details = useMemo(() => {
    const generalDetails = [getField('gender')];

    const buildShiftsData = (payload) => {
      const {
        date,
        startTime,
        endTime = null,
        locationId = null,
        shiftLocationId = null,
        positionId = null,
        clientBranchId = null,
        clientProjectId = null
      } = payload || {};
      const dateTime = `${moment(date).format(dateFormat)}
      (${formattingMap.startAndEndTime(startTime, endTime)})`;

      let locationItem = '';

      // shift location
      if (shiftLocationId && mappedLocations[shiftLocationId]) {
        locationItem = `${mappedLocations[shiftLocationId]?.name}`;
      }

      // project location
      if (locationId && mappedLocations[locationId] && locationId !== shiftLocationId) {
        locationItem += ` (${mappedLocations[locationId]?.name})`;
      }

      const position = [
        positionId && mappedSupplierPositions[positionId] ? `${mappedSupplierPositions[positionId]?.label}` : '',
        locationItem
      ]
        .filter((str) => str.length > 0)
        .join(' - ');

      const link =
        clientBranchId && clientProjectId
          ? `/${accountId}/scheduling/branch/${clientBranchId}/project/${clientProjectId}`
          : null;

      return { dateTime, position, link };
    };

    const upcomingShifts = (jobSummary || [])
      .map((payload) => ({ ...payload, date: moment(payload.date) }))
      .filter(({ date }) => {
        const formattedDate = moment(date).format(API_DATE_FORMAT);
        const formattedCurrentDate = moment().format(API_DATE_FORMAT);

        return moment(formattedDate).isSameOrAfter(moment(formattedCurrentDate));
      })
      .sort(({ date: d1 }, { date: d2 }) => (d1.isBefore(d2) ? -1 : 1))
      .map((payload) => buildShiftsData(payload));

    const pastShifts = (jobSummary || [])
      .map((payload) => ({ ...payload, date: moment(payload.date) }))
      .filter(({ date }) => {
        const formattedDate = moment(date).format(API_DATE_FORMAT);
        const formattedCurrentDate = moment().format(API_DATE_FORMAT);

        return moment(formattedDate).isBefore(moment(formattedCurrentDate));
      })
      .sort(({ date: d1 }, { date: d2 }) => (d1.isBefore(d2) ? 1 : -1))
      .map((payload) => buildShiftsData(payload));

    const personalInformation = [
      ...[
        phone
          ? {
              label: t('phone'),
              value: { title: parsePhoneNumberFromString(`+${phone}`)?.formatNational?.() },
              showContextMenu: true,
              Icon: AppMenuIcon,
              isInline: true
            }
          : []
      ]
    ];

    const jobSummaryDetails = [
      { label: t('upcomingShifts'), value: upcomingShifts },
      { label: t('pastShifts'), value: pastShifts }
    ].filter((detail) => detail.value.length > 0);

    const getOtherFieldsIcon = (fieldName) => {
      switch (fieldName) {
        case 'gender':
          return {
            Icon: EmployeeIcon
          };

        case 'birthdate':
          return {
            Icon: BalloonIcon
          };

        case 'employee-id':
          return {
            Icon: IdIcon
          };
        case 'start-employment-date':
          return {
            Icon: UnCheckedCalendarViewIcon
          };
        case 'end-employment-date':
          return {
            Icon: UnCheckedCalendarViewIcon
          };

        default:
          return {};
      }
    };
    const otherFields = fieldsMetadata
      .filter(
        ({ slug }) =>
          !['gender', 'birthdate', 'employee-id', 'start-employment-date', 'end-employment-date'].includes(slug)
      )
      .map(({ slug, name }) => {
        return { ...getField(slug || name), ...getOtherFieldsIcon(slug || name) };
      });

    return [
      { title: t('generalDetails'), details: generalDetails },
      { title: t('fields'), details: otherFields },
      ...(jobSummaryDetails.length > 0
        ? [
            {
              title: t('jobSummary'),
              details: {
                component: (
                  <>
                    {jobSummaryDetails.map((jobSummaryProp) => (
                      <DetailsBlockWrapper key={jobSummaryProp.label}>
                        <DetailBlockText>{jobSummaryProp.label}</DetailBlockText>
                        <Chips>
                          {jobSummaryProp.value.map((item, index) => (
                            <DetailsBlockChip
                              $disableClick={!item.link}
                              onClick={() => item.link && window.open(item.link, '_blank')}
                              key={index}
                              $isColored>
                              <DetailBlockSubText>{item.dateTime}</DetailBlockSubText>
                              <DetailBlockTitle>{item.position}</DetailBlockTitle>
                            </DetailsBlockChip>
                          ))}
                        </Chips>
                      </DetailsBlockWrapper>
                    ))}
                  </>
                )
              }
            }
          ]
        : []),
      ...(photos?.length > 0
        ? [
            {
              title: t('photos'),
              details: {
                component: <PhotoGallery photos={photos} avatar={image} setIsPhotoGalleryOpen={setIsPhotoGalleryOpen} />
              }
            }
          ]
        : []),
      { title: t('personalInformation'), details: personalInformation },
      { title: t('compliance'), details: employeeComplianceDataStatus }
    ];
  }, [
    getField,
    jobSummary,
    phone,
    t,
    fieldsMetadata,
    photos,
    image,
    dateFormat,
    mappedLocations,
    accountId,
    employeeComplianceDataStatus,
    formattingMap,
    mappedSupplierPositions
  ]);

  const { openUserChat, hasViewChatPrivilege } = useChatIframe();
  const options = useMemo(
    () => [
      ...(hasViewChatPrivilege
        ? [
            {
              label: t('chat'),
              icon: FilledChatIcon,
              disabled: !userId,
              onClick: () => {
                onClose();
                openUserChat(userId, accountId);
              }
            }
          ]
        : []),
      {
        label: t('whatsapp'),
        disabled: !phone,
        icon: WhatsappIcon,
        onClick: () => window.open(`https://wa.me/${phone}`, '_blank')?.focus()
      },
      {
        label: t('iMessage'),
        disabled: !phone,
        icon: PhoneMenuIconSmall,
        onClick: () => window.open(`sms:${phone};`, '_blank')?.focus()
      },
      {
        label: t('call'),
        disabled: !phone,
        icon: PhoneIcon,
        onClick: () => window.open(`tel:${phone}`, '_blank')?.focus()
      }
    ],
    [accountId, hasViewChatPrivilege, openUserChat, userId, onClose, phone, t]
  );

  useEffect(() => {
    attachModal({ onClose });

    return () => {
      detachModal({ onClose });
    };
    // ignore onClose as in most cases it's not memoized and causes infinite rerender
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachModal, detachModal]);

  if (isRestrictedFromEmployees) {
    return null;
  }

  return (
    <Portal>
      <Wrapper>
        <Container ref={ref}>
          <CloseButton icon={CloseIcon} onClick={onClose} />
          {isLoading ? (
            <LoaderWrapper>
              <Loader />
            </LoaderWrapper>
          ) : (
            <>
              <ShortInfo>
                <Avatar firstName={firstName} lastName={lastName} image={image} size={160} />
                <Name>{getEmployeeDisplayName(firstName, lastName, isShowLastNameFirst)}</Name>
                <External>{t('external')}</External>
                <InfoRow>
                  {rating > 0 && (
                    <RatingWrapper>
                      <StarIcon />
                      <SmallSemiBold>{rating}</SmallSemiBold>
                    </RatingWrapper>
                  )}

                  {lastActivity && (
                    <LastActivity>
                      {t('lastActivity')}: <TimeAgoRenderer {...lastActivityProps} />
                    </LastActivity>
                  )}
                </InfoRow>
              </ShortInfo>

              <Accordion defaultOpenRows={details.reduce((all, item, index) => ({ ...all, [index]: true }), {})}>
                {details
                  .filter(
                    (detailRow) =>
                      detailRow.custom ||
                      detailRow.details.component ||
                      (Array.isArray(detailRow.details) && detailRow.details.some((detail) => detail?.value))
                  )
                  .map(({ title, details: rowDetails }, detailIndex) => (
                    <Accordion.Box title={title} key={detailIndex}>
                      {rowDetails.component}
                      {!rowDetails.component &&
                        rowDetails
                          .filter((detail) => detail?.value)
                          .map(
                            (
                              { label, value, showContextMenu, Icon, onClick, isInline, isCompliance },
                              rowDetailsIndex
                            ) => (
                              <DetailRow
                                onClick={onClick}
                                key={rowDetailsIndex}
                                label={label}
                                value={value}
                                Icon={Icon}
                                isInline={isInline}
                                isCompliance={isCompliance}
                                options={showContextMenu && options}
                                onPreviewOpenChange={setIsDocumentPreviewOpen}
                              />
                            )
                          )}
                    </Accordion.Box>
                  ))}
              </Accordion>
            </>
          )}
        </Container>
      </Wrapper>
    </Portal>
  );
};

export default OSProfileSidebar;
